import React, { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Components that are used immediately
import ProtectedRoute from "./components/ProtectedRoute";
import Layout from "./components/Layout";
import SplashScreen from "./components/SplashScreen";
import Login from "./pages/Login";
import Recovery from "./pages/Recovery";

// Context Providers
import { AuthProvider } from "./context/AuthContext";
import { PWAProvider } from "./context/PWAContext";
import { WebSocketProvider } from "./context/WebSocketContext";
import { LocalProvider } from "./context/LocalContext";
import { pwaService } from "./services/pwaService";

// Lazy loaded components
const Home = lazy(() => import("./pages/Home"));
const HabilitarMaquina = lazy(() => import("./pages/HabilitarMaquina"));
const Premios = lazy(() => import("./pages/premios/Premios"));
const MovimientosCaja = lazy(() => import("./pages/MovimientosCajas"));
const MovimientosClientes = lazy(() => import("./pages/MovimientosClienes"));
const RegistrarMovimiento = lazy(() => import("./pages/RegistrarMovimiento"));
const AgregarCliente = lazy(() => import("./pages/AgregarCliente"));
const Caja = lazy(() => import("./pages/Caja"));
const AbrirCaja = lazy(() => import("./pages/AbrirCaja"));
const CerrarCaja = lazy(() => import("./pages/CerrarCaja"));
const Settings = lazy(() => import("./pages/Settings"));
const CashSummaryPage = lazy(() => import("./pages/CashSumary"));
const GameShopDemo = lazy(() => import("./pages/shop/GameShopDemo"));
const ClientSearchPage = lazy(() => import("./pages/shop/ClientSearchPage"));
const GameCatalogPage = lazy(() => import("./pages/shop/GameCatalogPage"));
const GamePlayPage = lazy(() => import("./pages/shop/GamePlayPage"));
const SalesPage = lazy(() => import("./pages/shop/SalesPage"));

// Lazy load all money-related pages
const DineroPages = {
  Ingreso: lazy(() => import("./pages/dinero/Ingreso")),
  Egreso: lazy(() => import("./pages/dinero/Egreso")),
  PagoManual: lazy(() => import("./pages/dinero/PagoManual")),
  Corte: lazy(() => import("./pages/dinero/Corte")),
};

// Lazy load all ficha-related pages
const FichaPages = {
  Ingreso: lazy(() => import("./pages/ficha/Ingreso")),
  Corte: lazy(() => import("./pages/ficha/Corte")),
};



// Tarjetas (Cards) related pages
const TarjetasPages = {
  ListaTarjetas: lazy(() => import("./pages/tarjetas/ListaTarjetas")),
  AsignarTarjeta: lazy(() => import("./pages/tarjetas/AsignarTarjeta"))
};

// Movimientos (Movements) related pages
const MovimientosPages = {
  FichaIngreso: lazy(() => import("./pages/movimientos/FichaIngreso")),
  FichaEgreso: lazy(() => import("./pages/movimientos/FichaEgreso")),
  PagoPremio: lazy(() => import("./pages/movimientos/PagoPremio")),
  Acreditacion: lazy(() => import("./pages/movimientos/Acreditacion"))
};

// Máquinas (Machines) related pages
const MaquinasPages = {
  HabilitarMaquinaCliente: lazy(() => import("./pages/maquinas/HabilitarMaquina")),
  ListaMaquinasCliente: lazy(() => import("./pages/maquinas/ListaMaquinas"))
};

// Kiosco related pages
const KioscoPages = {
  KioscoHome: lazy(() => import("./pages/kiosco/KioscoHome")),
  KioscoLoad: lazy(() => import("./pages/kiosco/KioscoLoad")),
  KioscoWithdraw: lazy(() => import("./pages/kiosco/KioscoWithdraw")),
  KioscoHistory: lazy(() => import("./pages/kiosco/KioscoHistory")),
  KioscoScanner: lazy(() => import("./pages/kiosco/KioscoScanner")),
  KioscoAssign: lazy(() => import("./pages/kiosco/KioscoAssign"))
};


// Loading component for suspense fallback
const LoadingSpinner = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
  </div>
);

const App: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnlineStatus = (online: boolean) => () => setIsOnline(online);

    // Initialize PWA and set up event listeners
    const initialize = async () => {
      try {
        await pwaService.register();
        window.addEventListener("online", handleOnlineStatus(true));
        window.addEventListener("offline", handleOnlineStatus(false));

        return () => {
          window.removeEventListener("online", handleOnlineStatus(true));
          window.removeEventListener("offline", handleOnlineStatus(false));
        };
      } catch (error) {
        console.error("Initialization error:", error);
      }
    };

    initialize();
  }, []);

  if (isLoading) {
    return <SplashScreen onFinish={() => setIsLoading(false)} />;
  }

  return (
    <AuthProvider>
      <LocalProvider>
        <PWAProvider>
          <WebSocketProvider>
          <Router>
            <div className="app-container">
              {!isOnline && (
                <div className="fixed top-0 left-0 right-0 bg-yellow-500 text-white text-center py-2 z-50">
                  No hay conexión a Internet - Modo fuera de línea
                </div>
              )}

              <Layout>
                <Suspense fallback={<LoadingSpinner />}>
                  <Routes>
                    {/* Public routes */}
                    <Route path="/demo" element={<GameShopDemo />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/recovery" element={<Recovery />} />

                    {/* Protected routes */}
                    <Route element={<ProtectedRoute />}>
                      <Route path="/" element={<Home />} />
                      <Route
                        path="/habilitar-maquina"
                        element={<HabilitarMaquina />}
                      />
                      <Route
                        path="/movimientos/cajas"
                        element={<MovimientosCaja />}
                      />
                      <Route
                        path="/registrar-movimiento"
                        element={<RegistrarMovimiento />}
                      />
                      <Route
                        path="/movimientos/clientes"
                        element={<MovimientosClientes />}
                      />
                      <Route path="/premios" element={<Premios />} />
                      <Route
                        path="/resumen/:id"
                        element={<CashSummaryPage />}
                      />
                      <Route
                        path="/agregar-cliente"
                        element={<AgregarCliente />}
                      />
                      <Route path="/caja" element={<Caja />} />
                      <Route path="/caja/abrir-caja" element={<AbrirCaja />} />
                      <Route
                        path="/caja/cerrar-caja"
                        element={<CerrarCaja />}
                      />

                      {/* Money management routes */}
                      <Route
                        path="/caja/dinero/ingreso"
                        element={<DineroPages.Ingreso />}
                      />
                      <Route
                        path="/caja/dinero/egreso"
                        element={<DineroPages.Egreso />}
                      />
                      <Route
                        path="/caja/dinero/corte"
                        element={<DineroPages.Corte />}
                      />
                      <Route
                        path="/caja/dinero/pago-manual"
                        element={<DineroPages.PagoManual />}
                      />

                      {/* Tarjetas routes */}
                      <Route
                        path="/tarjetas/lista"
                        element={<TarjetasPages.ListaTarjetas />}
                      />
                      <Route
                        path="/tarjetas/asignar"
                        element={<TarjetasPages.AsignarTarjeta />}
                      />

                      {/* Movimientos de fichas routes */}
                      <Route
                        path="/movimientos/fichas/ingreso"
                        element={<MovimientosPages.FichaIngreso />}
                      />
                      <Route
                        path="/movimientos/fichas/egreso"
                        element={<MovimientosPages.FichaEgreso />}
                      />

                      {/* Créditos routes */}
                      <Route
                        path="/creditos/pago-premio"
                        element={<MovimientosPages.PagoPremio />}
                      />
                      <Route
                        path="/creditos/acreditacion"
                        element={<MovimientosPages.Acreditacion />}
                      />

                      {/* Máquinas routes */}
                      <Route
                        path="/maquinas/habilitar"
                        element={<MaquinasPages.HabilitarMaquinaCliente />}
                      />

                         <Route
                        path="/maquinas/lista"
                        element={<MaquinasPages.ListaMaquinasCliente />}
                      />

                      {/* Ficha management routes */}
                      <Route
                        path="/caja/fichas/ingreso"
                        element={<FichaPages.Ingreso />}
                      />
                      <Route
                        path="/caja/fichas/corte"
                        element={<FichaPages.Corte />}
                      />

                      {/* Game shop routes */}
                      <Route path="/shop" element={<ClientSearchPage />} />
                      <Route
                        path="/shop/catalog/:clientId"
                        element={<GameCatalogPage />}
                      />
                      <Route path="/shop/ventas" element={<SalesPage />} />

                      <Route
                        path="/shop/play/:clientId/:gameId"
                        element={<GamePlayPage />}
                      />

                      {/* Kiosco Routes */}
                      <Route path="/kiosco" element={<KioscoPages.KioscoHome />} />
                      <Route path="/kiosco/load" element={<KioscoPages.KioscoLoad />} />
                      <Route path="/kiosco/withdraw" element={<KioscoPages.KioscoWithdraw />} />
                      <Route path="/kiosco/history" element={<KioscoPages.KioscoHistory />} />
                      <Route path="/kiosco/scanner/:operation" element={<KioscoPages.KioscoScanner />} />
                      <Route path="/kiosco/scanner/assign" element={<KioscoPages.KioscoAssign />} />

                      {/* Settings */}
                      <Route path="/configuracion" element={<Settings />} />
                    </Route>
                  </Routes>
                </Suspense>
              </Layout>

              <ToastContainer
                position="top-center"
                autoClose={3000}
                theme="light"
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
              />
            </div>
          </Router>
        </WebSocketProvider>
      </PWAProvider>
    </LocalProvider>
  </AuthProvider>
  );
};

export default App;
